import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingScreen from '../components/loading-screen';
//
import { useEffect } from 'react';
import { PATH_DASHBOARD, PATH_WEBINAR_PRESENTER } from 'src/routes/paths';
import { useAuthContext } from './useAuthContext';
import { useUserContext } from './useUserContext';

// ----------------------------------------------------------------------

type GuestGuardProps = {
  children: React.ReactNode;
};

export default function GuestGuard({ children }: GuestGuardProps) {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { organizationId } = useParams();
  const { isInitialized, isAuthenticated } = useAuthContext();
  const { data, isLoading, isUserRole } = useUserContext();
  const isLoginFromSearch =
    organizationId &&
    pathname.includes(`search/results/${organizationId}/booking`);
  const isClinicPage = pathname.startsWith('/placowka/');

  useEffect(() => {
    if (
      isInitialized &&
      isAuthenticated &&
      !isLoading &&
      data &&
      isLoginFromSearch &&
      isUserRole
    ) {
      return navigate(
        `${PATH_DASHBOARD.general.search.booking(organizationId)}${search}`,
        {
          replace: true,
        },
      );
    }

    if (
      isInitialized &&
      isAuthenticated &&
      !isLoading &&
      data &&
      isClinicPage &&
      isUserRole
    ) {
      const slug = pathname.replace('/placowka/', '');
      const dashboardPath = PATH_DASHBOARD.general.search.organization(slug);
      const queryString = search || '?type=all';
      return navigate(`${dashboardPath}${queryString}`, {
        replace: true,
      });
    }

    // The dashboard would detour a presenter through clinic onboarding.
    if (
      isInitialized &&
      isAuthenticated &&
      !isLoading &&
      data &&
      new URLSearchParams(search).get('redirect') === PATH_WEBINAR_PRESENTER
    ) {
      return navigate(PATH_WEBINAR_PRESENTER, { replace: true });
    }

    if (
      isInitialized &&
      isAuthenticated &&
      !isLoading &&
      data
    ) {
      return navigate(
        search ? `${PATH_DASHBOARD.root}${search}` : PATH_DASHBOARD.root,
        { replace: true },
      );
    }
  }, [
    isInitialized,
    isAuthenticated,
    isLoading,
    data,
    navigate,
    organizationId,
    isLoginFromSearch,
    isClinicPage,
    pathname,
    search,
    isUserRole,
  ]);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  return <> {children} </>;
}
